// Business Administration Custom Hook
// Feature: 005-business-administration
// Updated: 007-cloudflare-migration - added i18n error messages

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import type {
  BusinessSummary,
  BusinessWithOwner,
  BusinessStatus,
  BusinessListResponse,
  BusinessProfileUpdate,
  BusinessPhotoOperation,
  CategoryOption,
} from '../types/businessAdmin';
import * as businessService from '../services/businessAdmin';
import { isApiError, ApiErrorCode } from '../services/api/errors';

/**
 * Get localized error message from API error
 */
function getErrorMessage(
  error: unknown,
  t: (key: string) => string,
  fallbackKey: string
): string {
  if (isApiError(error)) {
    // Try common namespace first for API errors
    const errorKey = `common:api.errors.${error.code}`;
    const translated = t(errorKey);
    // If translation exists (not same as key), use it
    if (translated !== errorKey) {
      return translated;
    }
    // Fall back to API error message
    return error.message;
  }
  return t(fallbackKey);
}

export function useBusinessAdministration() {
  const { t } = useTranslation('businessAdmin');

  // State
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);
  const [rawBusinesses, setRawBusinesses] = useState<BusinessSummary[]>([]); // Before category enrichment
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessWithOwner | null>(null);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<BusinessStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Retry state for exponential backoff
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  // Load businesses list with retry
  const loadBusinesses = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response: BusinessListResponse = await businessService.getAllBusinesses(
        searchTerm,
        statusFilter,
        currentPage
      );

      setRawBusinesses(response.businesses);
      setTotalPages(response.pagination.totalPages);
      setTotalCount(response.pagination.totalCount);
      retryCountRef.current = 0; // Reset retry count on success
    } catch (err) {
      const errorMessage = getErrorMessage(err, t, 'messages.loadError');
      setError(errorMessage);

      // Retry with exponential backoff for network/server errors
      if (isApiError(err) && 
          (err.code === ApiErrorCode.NETWORK_ERROR || err.code === ApiErrorCode.SERVER_ERROR) &&
          retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        const delay = Math.pow(2, retryCountRef.current) * 1000; // 2s, 4s, 8s
        
        Sentry.addBreadcrumb({
          category: 'business-admin',
          message: `Retrying loadBusinesses (attempt ${retryCountRef.current})`,
          level: 'info',
          data: { delay },
        });

        setTimeout(() => {
          loadBusinesses();
        }, delay);
      }
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, currentPage]);

  // Load categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await businessService.getCategories();
        setCategories(cats);
      } catch (err) {
        Sentry.addBreadcrumb({
          category: 'business-admin',
          message: 'Failed to load categories',
          level: 'warning',
        });
      }
    };
    fetchCategories();
  }, []);

  // Load businesses when filters change
  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  // Enrich businesses with category names from loaded categories
  useEffect(() => {
    if (rawBusinesses.length === 0) {
      setBusinesses([]);
      return;
    }

    const enriched = rawBusinesses.map(business => {
      // business.category contains UUID from API, look up name from categories
      if (categories.length > 0) {
        const matchedCategory = categories.find(cat => cat.id === business.category);
        if (matchedCategory) {
          return { ...business, category: matchedCategory.name };
        }
      }
      return business;
    });

    setBusinesses(enriched);
  }, [rawBusinesses, categories]);

  // Select a business and load its details
  const selectBusiness = useCallback(async (businessId: string) => {
    setIsLoadingDetails(true);
    setError(null);
    setIsEditing(false);

    try {
      const details = await businessService.getBusinessDetails(businessId);
      if (!details) {
        setError(t('messages.notFound'));
        return;
      }

      setSelectedBusiness(details);
    } catch (err) {
      const errorMessage = getErrorMessage(err, t, 'messages.loadDetailsError');
      setError(errorMessage);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [t]);

  // Clear selected business (back to list)
  const clearSelectedBusiness = useCallback(() => {
    setSelectedBusiness(null);
    setIsEditing(false);
  }, []);

  // Change business status
  const changeStatus = useCallback(
    async (newStatus: BusinessStatus, reason?: string) => {
      if (!selectedBusiness) return { success: false };

      try {
        const result = await businessService.changeBusinessStatus({
          businessId: selectedBusiness.id,
          newStatus,
          reason,
        });

        if (result.success) {
          // Refresh business details and list
          await selectBusiness(selectedBusiness.id);
          await loadBusinesses();
        } else if (result.error) {
          setError(result.error);
        }

        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'messages.statusChangeError');
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [selectedBusiness, selectBusiness, loadBusinesses, t]
  );

  // Approve business
  const approveBusiness = useCallback(
    async () => changeStatus('active'),
    [changeStatus]
  );

  // Disable business
  const disableBusiness = useCallback(
    async (reason: string) => changeStatus('disabled', reason),
    [changeStatus]
  );

  // Reactivate business
  const reactivateBusiness = useCallback(
    async () => changeStatus('active'),
    [changeStatus]
  );

  // Start editing
  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  // Save business profile edits with photos
  const saveBusinessProfile = useCallback(
    async (updates: BusinessProfileUpdate, newPhotos: File[] = []) => {
      if (!selectedBusiness) return { success: false };

      try {
        // First save profile updates
        const profileResult = await businessService.updateBusinessProfile(
          selectedBusiness.id,
          updates
        );

        if (!profileResult.success) {
          return profileResult;
        }

        // Then handle photo uploads if any
        if (newPhotos.length > 0) {
          const photoResult = await businessService.updateBusinessPhotos(
            selectedBusiness.id,
            { add: newPhotos }
          );

          if (!photoResult.success) {
            return { success: false, errors: { photos: photoResult.error || t('messages.photoUploadError') } };
          }
        }

        // Refresh business details and list
        await selectBusiness(selectedBusiness.id);
        await loadBusinesses();
        setIsEditing(false);

        return { success: true };
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'messages.saveError');
        return { success: false, errors: { general: errorMessage } };
      }
    },
    [selectedBusiness, selectBusiness, loadBusinesses, t]
  );

  // Update business photos
  const updatePhotos = useCallback(
    async (operations: BusinessPhotoOperation) => {
      if (!selectedBusiness) return { success: false };

      try {
        const result = await businessService.updateBusinessPhotos(
          selectedBusiness.id,
          operations
        );

        if (result.success) {
          // Refresh business details
          await selectBusiness(selectedBusiness.id);
        }

        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'messages.photoUpdateError');
        return { success: false, error: errorMessage };
      }
    },
    [selectedBusiness, selectBusiness, t]
  );

  // Pagination handlers
  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const previousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  return {
    // State
    businesses,
    selectedBusiness,
    categories,
    searchTerm,
    statusFilter,
    currentPage,
    totalPages,
    totalCount,
    isLoading,
    isLoadingDetails,
    isEditing,
    error,

    // Actions
    setSearchTerm,
    setStatusFilter,
    selectBusiness,
    clearSelectedBusiness,
    approveBusiness,
    disableBusiness,
    reactivateBusiness,
    startEditing,
    cancelEditing,
    saveBusinessProfile,
    updatePhotos,
    goToPage,
    nextPage,
    previousPage,
    refreshBusinesses: loadBusinesses,
  };
}
